// vue
import { useStore } from '@/store';

// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';

// models
import { ImgTournament } from '@/vue3/api/models';

export const useEuropeTourService = () => {
    // hooks
    const axios = useAxios();
    const { apiUrl } = useStore().state;

    // methods
    const populateEuropeTourData = async (
        tournamentId: number,
    ): Promise<ImgTournament[]> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/${tournamentId}/populate`,
            null,
        );
        return res.data;
    };

    const getEurpeTourCoursesData = async (): Promise<any> => {
        const res = await axios.get(`${apiUrl}/europetour/courses`);
        return res.data;
    };

    const getEurpeTourEventData = async (): Promise<any> => {
        const res = await axios.get(`${apiUrl}/europetour/events/completed`);
        return res.data;
    };

    const mapCourseSchemaEuroCourseId = async (
        courseId: any,
        eventId: any,
    ): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/course/${courseId}/eurocourse/${eventId}`,
            null,
        );
        return res.data;
    };

    const startHarvester = async (tourId: any): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/harvestor/start/${tourId}`,
            null,
        );
        return res.data;
    };

    const stopHarvester = async (): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/harvestor/stop`,
            null,
        );
        return res.data;
    };

    const getLatestBallPosition = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/europetour/tournament/course/${id}/ball`,
        );
        return res.data;
    };

    const getInProgressEvents = async (id: number): Promise<any> => {
        const res = await axios.get(`${apiUrl}/europetour/course/${id}/events`);
        return res.data;
    };
    const getAllShotData = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/europetour/tournament/course/${id}/allball`,
        );
        return res.data;
    };
    const getTeetimesData = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/europetour/tournament/course/${id}/teetimes`,
        );
        return res.data;
    };
    const getAllShotDataFromDB = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/europetour/tournament/course/${id}/allballdb`,
        );
        return res.data;
    };
    const getTournamentCurrentRoundNo = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/europetour/tournament/course/${id}/roundNo`,
        );
        return res.data;
    };
    const populateAllShotData = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/europetour/tournament/course/populate/${id}`,
        );
        return res.data;
    };

    const updateAndFetchCourseEventData = async (id: number): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/course/update/${id}`,
            null,
        );
        return res.data;
    };

    const startIMGHarvester = async (tourId: any): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/img/harvestor/start/${tourId}`,
            null,
        );
        return res.data;
    };

    const stopIMGHarvester = async (): Promise<any> => {
        const res = await axios.post(`${apiUrl}/img/harvestor/stop`, null);
        return res.data;
    };
    const getPaceOfPlayData = async (id: number): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/course/${id}/PaceOfPlay`,
            null,
        );
        return res.data;
    };

    const getPaceOfPlayJsonData = async (id: number): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/course/${id}/PaceOfPlay/json`,
            null,
        );
        return res.data;
    };

    const getPaceOfPlaySummary = async (
        events: { eventId: number; rounds: number[] }[],
    ): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/summary`,
            { events },
        );
        return res.data;
    };

    const getPaceOfPlayShots = async (ids: number[]): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/shots`,
            { eventIds: ids },
        );
        return res.data;
    };

    const getPaceOfPlayKeptShots = async (ids: number[]): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/shots/kept`,
            { eventIds: ids },
        );
        return res.data;
    };

    const getPaceOfPlayMatchShots = async (ids: number[]): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/shots/match`,
            { eventIds: ids },
        );
        return res.data;
    };

    const getPaceOfPlayPlayerShots = async (ids: number[]): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/shots/player`,
            { eventIds: ids },
        );
        return res.data;
    };

    const populatePaceOfPlay = async (): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/db/populate`,
            null,
        );
        return res.data;
    };

    const populatePaceOfPlayLive = async (): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/live/populate`,
            null,
        );
        return res.data;
    };

    const downloadPaceOfPlayWorkbook = async (ids: number[]): Promise<void> => {
        const res = await axios.post(
            `${apiUrl}/europetour/tournament/PaceOfPlay/zip`,
            { eventIds: ids },
            undefined,
            { responseType: 'blob' },
        );
        const url = URL.createObjectURL(
            new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = 'PaceOfPlay.xlsx';
        a.click();
        URL.revokeObjectURL(url);
    };

    return {
        getAllShotData,
        getAllShotDataFromDB,
        stopHarvester,
        startHarvester,
        getInProgressEvents,
        getLatestBallPosition,
        populateEuropeTourData,
        getEurpeTourCoursesData,
        getEurpeTourEventData,
        mapCourseSchemaEuroCourseId,
        getTournamentCurrentRoundNo,
        populateAllShotData,
        updateAndFetchCourseEventData,
        startIMGHarvester,
        stopIMGHarvester,
        getTeetimesData,
        getPaceOfPlayData,
        getPaceOfPlayJsonData,
        getPaceOfPlaySummary,
        getPaceOfPlayShots,
        getPaceOfPlayKeptShots,
        getPaceOfPlayMatchShots,
        getPaceOfPlayPlayerShots,
        populatePaceOfPlay,
        populatePaceOfPlayLive,
        downloadPaceOfPlayWorkbook,
    };
};
